import { useEffect, useLayoutEffect, useState } from "react";
import { App } from "@capacitor/app";
import { Capacitor, PluginListenerHandle } from "@capacitor/core";
import { LocalNotifications } from "@capacitor/local-notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { AndroidSettings, IOSSettings, NativeSettings } from "capacitor-native-settings";
import { Alert, Colors } from "swing-components";

import {
  AlertError,
  ButtonScoreWrapper,
  ContentSingleColumn,
  ModalContentPhoneNumberEdit,
  RadioInput,
  Toggle,
  useAuth,
  useModal,
} from "~components";
import { GET, NotificationsPayload, ProfileData, PUT, translate } from "~utils";

import { ScorePage } from "../../ScoreTemplates";
import styles from "./Notifications.module.css";

export const NotificationType = {
  SMS: "sms",
  PUSH: "push",
} as const;

// Note: The type on /api/sub/me is sms?: boolean while on /api/sub/notifications it's sms: boolean.
type NotificationSettings = {
  sms?: boolean;
  push?: boolean;
};

type NotificationTypeKey = (typeof NotificationType)[keyof typeof NotificationType];

type Notification = {
  type: NotificationTypeKey;
  label: string;
  subText?: JSX.Element | string;
  isDisabled: boolean;
};

type NotificationsViewProps = {
  phoneNumber?: string;
  isLoading?: boolean;
  isPhoneNumberVerified?: boolean;
  notificationSettings?: NotificationSettings;
  isAlias?: boolean;
  refreshUser: () => void;
  isPushPermissionGranted?: boolean;
  isWeb?: boolean;
};

export function Notifications() {
  const {
    userInfo: { isPhoneNumberVerified, isAlias, notificationsSettings },
    refreshUser,
  } = useAuth();

  const { data: userData, isLoading } = useQuery<{ data: ProfileData }>({
    queryKey: ["fetchProfile"],
    queryFn: () => GET("/api/sub/profile"),
    enabled: !isPhoneNumberVerified,
  });

  const [isPushPermissionGranted, setIsPushPermissionGranted] = useState<boolean>(false);
  const [isPermissionLoading, setIsPermissionLoading] = useState(true);

  useEffect(() => {
    async function fetchPermissions() {
      const permission = await getPushPermissionStatus();
      setIsPushPermissionGranted(permission);
      setIsPermissionLoading(false);
    }

    fetchPermissions();
  }, []);

  useEffect(() => {
    // Recheck push permissions after a user returns to the app from device settings
    let appStateListener: PluginListenerHandle;

    const setupListener = async () => {
      appStateListener = await App.addListener("appStateChange", async ({ isActive }) => {
        if (!isActive) return;
        const permission = await getPushPermissionStatus();
        setIsPushPermissionGranted(permission);
      });
    };

    setupListener();

    return () => {
      appStateListener?.remove();
    };
  }, []);

  return (
    <NotificationsView
      isLoading={isLoading || isPermissionLoading}
      phoneNumber={userData?.data?.phoneNumber}
      isPhoneNumberVerified={isPhoneNumberVerified}
      notificationSettings={notificationsSettings}
      isAlias={isAlias}
      refreshUser={refreshUser}
      isPushPermissionGranted={isPushPermissionGranted}
      isWeb={Capacitor.getPlatform() === "web"}
    />
  );
}

export function NotificationsView(props: NotificationsViewProps) {
  const {
    isLoading,
    phoneNumber,
    isPhoneNumberVerified,
    notificationSettings,
    isAlias,
    refreshUser,
    isPushPermissionGranted,
    isWeb,
  } = props;

  const notificationTypes: Notification[] = [
    {
      type: NotificationType.SMS,
      label: translate("MORE_NOTIFICATIONS_TYPE_SMS"),
      subText: isPhoneNumberVerified ? undefined : (
        <>
          <span
            style={{ textDecoration: "underline", color: Colors.blue500, cursor: "pointer" }}
            onClick={() => openPhoneNumberEditModal()}
          >
            {translate("MORE_NOTIFICATIONS_TYPE_SMS_SUBTEXT_START")}
          </span>{" "}
          <span style={{ color: Colors.black }}>
            {translate("MORE_NOTIFICATIONS_TYPE_SMS_SUBTEXT_END")}
          </span>
        </>
      ),
      isDisabled: !isPhoneNumberVerified,
    },
    {
      type: NotificationType.PUSH,
      label: translate("MORE_NOTIFICATIONS_TYPE_PUSH"),
      subText: isWeb ? (
        translate("MORE_NOTIFICATIONS_TYPE_PUSH_WEB_SUBTEXT")
      ) : !isPushPermissionGranted ? (
        <>
          <span style={{ color: Colors.black }}>
            {translate("MORE_NOTIFICATIONS_TYPE_PUSH_MOBILE_SUBTEXT_START")}
          </span>{" "}
          <span
            style={{ textDecoration: "underline", color: Colors.blue500, cursor: "pointer" }}
            onClick={handleOpenDeviceSettings}
          >
            {translate("MORE_NOTIFICATIONS_TYPE_PUSH_MOBILE_SUBTEXT_END")}
          </span>
        </>
      ) : undefined,
      isDisabled: isWeb || !isPushPermissionGranted,
    },
  ];

  const pushEnabled = notificationSettings?.push;
  const smsEnabled = notificationSettings?.sms;
  const isToggleInitiallyOn = pushEnabled || smsEnabled;

  /* Hooks */
  const [isToggleOn, setIsToggleOn] = useState(isToggleInitiallyOn);
  const [selectedNotificationType, setSelectedNotificationType] = useState<
    NotificationTypeKey | undefined
  >(undefined);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const queryClient = useQueryClient();

  useLayoutEffect(() => {
    const updatedNotificationType = getInitialNotificationType(
      !!isPushPermissionGranted,
      notificationSettings,
    );
    setSelectedNotificationType(updatedNotificationType);
  }, [notificationSettings, isPushPermissionGranted]);

  const { mutate: updateNotificationsSettings, error: updateMutationError } = useMutation<
    { data: NotificationsPayload },
    AxiosError,
    NotificationsPayload
  >({
    mutationFn: (payload: NotificationsPayload) => {
      return PUT("/api/sub/notifications", payload);
    },
    onSuccess: (updatedSettingsData) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      queryClient.setQueryData(["userData"], (oldUserData: any) => {
        return {
          ...oldUserData,
          notificationsSettings: updatedSettingsData.data.notificationsSettings,
        };
      });
      // Using refreshUser to trigger a refetch of user data to
      // keep the `userInfo` in AuthProvider's context updated.
      refreshUser();
    },
  });

  useEffect(() => {
    // Reset pending changes when cache updates - the save button will be disabled to reflect their changes are the current
    setHasPendingChanges(false);
  }, [notificationSettings]);

  /* Modal */
  const { openModal, closeModal } = useModal({
    component: (
      <>
        {isModalOpen && (
          <ModalContentPhoneNumberEdit
            isPhoneNumberVerified={isPhoneNumberVerified}
            onDismiss={() => {
              setIsModalOpen(false);
              closeModal();
            }}
            phoneNumber={phoneNumber}
          />
        )}
      </>
    ),
    modalOptions: { cssClass: "modal-sub-profile-edit" },
  });

  const openPhoneNumberEditModal = () => {
    setIsModalOpen(true);
    openModal();
  };

  const handleToggleChange = (value: boolean) => {
    setIsToggleOn(value);
    setHasPendingChanges(true);
  };

  const handleNotificationTypeChange = (type: NotificationTypeKey) => {
    setSelectedNotificationType(type);
    setHasPendingChanges(true);
  };

  const handleSave = () => {
    let payload: NotificationsPayload;
    if (isToggleOn && selectedNotificationType) {
      payload = {
        notificationsSettings: {
          [selectedNotificationType]: true,
        },
      };
    } else {
      // Send empty payload to represent toggle off.
      // The BE will handle setting both types to false.
      payload = { notificationsSettings: {} };
    }
    updateNotificationsSettings(payload);
  };

  function handleOpenDeviceSettings() {
    NativeSettings.open({
      optionAndroid: AndroidSettings.AppNotification,
      // NOTE: We would like to switch this implementation to use the Capacitor App Launcher
      // plugin, in the future once we upgrade to Capacitor v6.
      optionIOS: IOSSettings.App,
    });
  }

  // Save is enabled only when there are changes.
  // If the toggle is on, a notification type must be selected to enable save.
  const isSaveEnabled =
    hasPendingChanges && (isToggleOn ? selectedNotificationType !== undefined : true);

  return (
    <ScorePage title={translate("MORE_NOTIFICATIONS")} isLoading={isLoading} hasBack>
      <ContentSingleColumn>
        <div className={styles.wrapper}>
          {updateMutationError && <AlertError />}
          {/* If aliased, show a warning to prevent super-admins from altering push notifications */}
          {isAlias && <SuperAdminNotificationsWarning />}
          <div className={styles.toggleContainer}>
            <Toggle
              label={translate("MORE_NOTIFICATIONS_TOGGLE_LABEL")}
              isSelected={isToggleOn}
              onToggleChange={handleToggleChange}
            />
          </div>
          {isToggleOn && (
            <>
              <p style={{ fontWeight: 600 }}>{translate("MORE_NOTIFICATIONS_TYPE_HEADER")}</p>
              <div className={styles.notificationsWrapper}>
                {notificationTypes.map((type, idx) => (
                  <div
                    key={`${type.type}`}
                    className={styles.notificationItem}
                    style={{
                      borderBottom:
                        idx < notificationTypes.length - 1
                          ? `1px solid ${Colors.slate200}`
                          : "none",
                    }}
                  >
                    <RadioInput
                      value={type.type}
                      label={type.label}
                      isDisabled={type.isDisabled}
                      isSelected={selectedNotificationType === type.type}
                      onChange={(value) =>
                        handleNotificationTypeChange(value as NotificationTypeKey)
                      }
                    />
                    {type.subText && (
                      <p
                        style={{
                          color: type.isDisabled ? Colors.slate300 : Colors.black,
                          fontSize: 12,
                          // Left margin of 36px to account for the radio button width and the gap (20px + 16px)
                          margin: "0 0 0 36px",
                          textAlign: "left",
                        }}
                      >
                        {type.subText}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
          <ButtonScoreWrapper
            buttonPrimary={{
              label: translate("LABEL_SAVE"),
              onClick: handleSave,
              disabled: !isSaveEnabled,
              expand: "block",
            }}
          />
        </div>
      </ContentSingleColumn>
    </ScorePage>
  );
}

function SuperAdminNotificationsWarning() {
  return (
    <div style={{ marginBottom: 24 }}>
      <Alert
        color="warning"
        title={translate("MORE_NOTIFICATIONS_SUPER_ADMIN_ALERT_TITLE")}
        showIcon
      >
        {translate("MORE_NOTIFICATIONS_SUPER_ADMIN_ALERT_MSG")}
      </Alert>
    </div>
  );
}

async function getPushPermissionStatus() {
  // On Android 12 and below, push notifications are always 'granted'
  // because they are enabled by default.
  // To check actual notification settings on older devices,
  // we have to use the LocalNotifications plugin instead. - https://capacitorjs.com/docs/apis/push-notifications#checkpermissions
  // LocalNotifications shows the true state for iOS as well, so we can use it for both platforms.

  if (Capacitor.getPlatform() === "web") return false;

  try {
    const permissions = await LocalNotifications.checkPermissions();
    return permissions.display === "granted";
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("Error checking push notification permissions: ", err);
    return false;
  }
}

export function getInitialNotificationType(
  isPushPermissionGranted: boolean,
  settings: NotificationSettings | undefined,
): NotificationTypeKey | undefined {
  if (!settings) return;
  const { push: pushEnabled, sms: smsEnabled } = settings;

  if (pushEnabled) {
    return isPushPermissionGranted
      ? NotificationType.PUSH
      : smsEnabled
        ? NotificationType.SMS
        : undefined;
  }

  return smsEnabled ? NotificationType.SMS : undefined;
}
